/**
 * Supabase Browser Client
 *
 * IMPORTANT: All Supabase clients (server, client, middleware) use the same custom
 * storage key 'sb-neutralaiz-auth-token'. This ensures cookies work consistently
 * across all environments regardless of which Supabase URL is used (cloud vs self-hosted).
 *
 * For new environment setup, see: k8s/overlays/{env}/patches/ and docs/NEW_ENVIRONMENT_RUNBOOK.md
 */
import { createBrowserClient } from '@supabase/ssr'
import {
    circuitBreakerDetail,
    classifyAlreadyUsed,
    classifyServerEndedSession,
    createAlreadyUsedTracker,
    isAlreadyUsedMessage,
    summarizeAuthErrorBody,
} from './refreshFailureClassifier'
import {
    broaderCookieDomains,
    isClearableAuthStorageKey,
    isOwnAuthCookieName,
    ownAuthCookieNamesForLegacyScopeCleanup,
    withoutCodeVerifierDeletions,
} from './authCookiePolicy'
import logger, { enterAuthErrorSuppression } from '@/lib/logger';
import { observeAuthState } from './sessionKeeper';
import { deriveSupabaseUrlFromHostname, resolveBrowserSupabaseUrl } from './browserUrl';
import { fetchWithDeadline, withDeadline } from '@/lib/http/fetchWithDeadline'
import { getFetchInputUrl, getFetchRequestSignal } from '@/lib/http/fetchRequest'
import { supabaseRequestDeadlineMs } from './requestDeadline'
import type { SupabaseClient } from '@supabase/supabase-js'
import {
  getCookieDomain as getEnvCookieDomain,
  getAllCookieDomains,
  getAuthStorageKey,
} from '@/lib/config/environment';

// Type declaration for runtime config is in global.d.ts
// window.__RUNTIME_CONFIG__ is injected by RuntimeConfig server component

// Singleton instance to prevent multiple clients causing refresh token race conditions
let supabaseClientInstance: SupabaseClient | null = null

// Token refresh lock to prevent concurrent refresh attempts
// When one request is refreshing, others wait for it to complete
let refreshPromise: Promise<Response> | null = null
let refreshPromiseUrl: string | null = null

// Circuit breaker for auth errors - prevents infinite 429 retry loops
let authErrorCount = 0
let lastAuthErrorTime = 0
const alreadyUsedTracker = createAlreadyUsedTracker()
const AUTH_ERROR_THRESHOLD = 5  // Increased from 3 to reduce false positives
const AUTH_ERROR_WINDOW_MS = 30000 // 30 seconds (increased from 10s)
const TOKENS_ONLY_MIGRATION_FLAG = 'naiz_tokens_only_cookie_migration_v1'
const OVERSIZED_SESSION_COOKIE_BYTES = 4500

type AuthStorageAdapter = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const FALLBACK_AUTH_STORAGE: AuthStorageAdapter = {
    getItem: () => null,
    setItem: () => { },
    removeItem: () => { },
}

/**
 * SSR-safe auth storage.
 * @supabase/ssr touches auth.userStorage during client creation when using
 * cookies.encode='tokens-only'. Provide an explicit adapter so server rendering
 * never attempts to read window.localStorage.
 */
function getAuthUserStorage(): AuthStorageAdapter {
    if (typeof window === 'undefined') {
        return FALLBACK_AUTH_STORAGE
    }

    try {
        return window.localStorage
    } catch (error) {
        logger.warn('Supabase', 'localStorage unavailable, using fallback auth storage', { error })
        return FALLBACK_AUTH_STORAGE
    }
}

function getSupabaseSessionCookieSize(): number {
    if (typeof document === 'undefined') return 0

    // Size OUR environment's session cookie ONLY (exact storage key + its
    // numbered chunks) — same rule as the middleware's ownAuthCookieBytes.
    // The old broad `sb-neutralaiz-auth-token` prefix also counted a legacy
    // parent-domain cookie AND the PKCE code-verifier, so a foreign cookie
    // could trip the migration threshold and make setSession() (a refresh)
    // run on page load — the client half of the 2026-08-19 refresh-race that
    // revoked the owner's token family. A foreign cookie is not ours to
    // migrate; it can never be shrunk by re-encoding OUR session anyway.
    const storageKey = getAuthStorageKey()
    return document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => {
            const name = cookie.split('=')[0]
            return name === storageKey || /^\.\d+$/.test(name.slice(storageKey.length)) && name.startsWith(`${storageKey}.`)
        })
        .reduce((total, cookie) => {
            const [name, ...valueParts] = cookie.split('=')
            return total + name.length + valueParts.join('=').length
        }, 0)
}

async function migrateOversizedSessionCookies(client: SupabaseClient): Promise<void> {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return

    const currentSize = getSupabaseSessionCookieSize()
    if (currentSize < OVERSIZED_SESSION_COOKIE_BYTES) return
    const flag = localStorage.getItem(TOKENS_ONLY_MIGRATION_FLAG)
    if (flag === 'done') return
    // 'running-<ts>' is a soft LOCK (Sol review 2026-08-19): a second tab
    // racing this migration is exactly the concurrent-refresh race that revokes
    // the token family. A lock in the FUTURE (clock skew/corruption) or older
    // than 5 minutes (crashed tab) is not trusted.
    const now = Date.now()
    if (flag?.startsWith('running-')) {
        const ts = Number(flag.slice('running-'.length))
        if (Number.isFinite(ts) && ts <= now && now - ts < 5 * 60 * 1000) return
    }

    // Prefer the Web Locks API where available — it is an ATOMIC cross-tab
    // lock, unlike the localStorage read-then-write which two tabs can both
    // pass. Fall back to the soft lock below where it is unavailable.
    const locks = (typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined)
    if (locks?.request) {
        await locks.request('naiz_tokens_only_cookie_migration', { ifAvailable: true }, async (lock) => {
            if (!lock) return // another tab holds it; skip
            await runOversizedSessionCookieMigration(client)
        })
        return
    }

    localStorage.setItem(TOKENS_ONLY_MIGRATION_FLAG, `running-${now}`)
    await runOversizedSessionCookieMigration(client)
}

/** The actual getSession -> setSession re-encode. Runs under whichever lock
 *  migrateOversizedSessionCookies acquired (Web Locks, or the soft flag). */
async function runOversizedSessionCookieMigration(client: SupabaseClient): Promise<void> {
    const beforeBytes = getSupabaseSessionCookieSize()
    try {
        const { data: { session } } = await client.auth.getSession()
        if (!session?.access_token || !session?.refresh_token) {
            localStorage.removeItem(TOKENS_ONLY_MIGRATION_FLAG)
            return
        }

        const { error } = await client.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
        })

        if (error) {
            throw error
        }

        const migratedSize = getSupabaseSessionCookieSize()
        logger.warn('Supabase', 'Migrated oversized auth cookies to tokens-only encoding', {
            beforeBytes,
            afterBytes: migratedSize,
        })
        localStorage.setItem(TOKENS_ONLY_MIGRATION_FLAG, 'done')
    } catch (error) {
        localStorage.removeItem(TOKENS_ONLY_MIGRATION_FLAG)
        logger.warn('Supabase', 'Failed to migrate oversized auth cookies', { error })
    }
}

/**
 * Extract Supabase project ID from URL
 * Supports both cloud and self-hosted formats:
 * - Cloud: https://uceqgobugaxbsbzuopbd.supabase.co -> uceqgobugaxbsbzuopbd
 * - Self-hosted: https://api-supabase.demo.neutralaiz.com -> neutralaiz
 */
function extractSupabaseProjectId(supabaseUrl: string | undefined): string | null {
    if (!supabaseUrl) return null

    // Try standard Supabase cloud format first: https://[projectid].supabase.co
    const cloudMatch = supabaseUrl.match(/https?:\/\/([a-z0-9]+)\.supabase\.co/i)
    if (cloudMatch) return cloudMatch[1]

    // Try self-hosted format: https://api-supabase.[env].[domain].com
    // Extract the domain name (e.g., "neutralaiz" from "api-supabase.demo.neutralaiz.com")
    const selfHostedMatch = supabaseUrl.match(/https?:\/\/api[-.]supabase\.[^.]+\.([a-z0-9-]+)\./i)
    if (selfHostedMatch) return selfHostedMatch[1]

    // Fallback: try to extract any meaningful identifier from the URL
    // e.g., https://supabase.neutralaiz.com -> neutralaiz
    const fallbackMatch = supabaseUrl.match(/https?:\/\/[^/]*\.([a-z0-9-]+)\.(com|io|dev|app)/i)
    if (fallbackMatch) return fallbackMatch[1]

    return null
}

/**
 * Clean up auth cookies from OTHER Supabase projects
 * ROOT CAUSE FIX: Multiple Supabase projects on same domain causes cookie bloat
 * Each service should only keep cookies from its own project
 */
function cleanupForeignProjectCookies(currentProjectId: string | null): void {
    if (typeof document === 'undefined' || !currentProjectId) return

    const expectedPrefix = `sb-${currentProjectId}-`
    // OUR cookies are the per-environment storage key (getAuthStorageKey()) and
    // its derivatives — the exact key, numeric chunks, -code-verifier, -user.
    // Any OTHER `sb-neutralaiz-auth-token*` cookie (the legacy bare pre-08-03
    // key, a sibling environment's leak) is dead weight that nothing reads and
    // that can push the jar over proxy/WebSocket header limits (431) — clean it
    // (Sol review 2026-08-19: the old blanket prefix exemption kept them alive
    // forever and one of them tripped the migration storm).
    const ownStorageKey = getAuthStorageKey()
    const isOurs = (name: string) =>
        isOwnAuthCookieName(name, ownStorageKey) ||
        name === `${ownStorageKey}-code-verifier` ||
        name === `${ownStorageKey}-user`
    // The legacy CUSTOM-key family (sb-neutralaiz-auth-token*) that is NOT ours
    // must be foreign REGARDLESS of the project-prefix exemption below — on an
    // env whose extracted project id is 'neutralaiz', expectedPrefix would be
    // `sb-neutralaiz-` and would otherwise re-exempt the very bare cookie that
    // caused the storm (Sol review 2026-08-19).
    const isLegacyCustomFamily = (name: string) =>
        name.startsWith('sb-neutralaiz-auth-token') && !isOurs(name)
    const cookies = document.cookie.split(';')
    const parsedCookies = cookies.map(cookie => {
        const [name, ...valueParts] = cookie.trim().split('=')
        return { name, value: valueParts.join('=') }
    })
    const foreignCookies: string[] = []

    for (const cookie of cookies) {
        const name = cookie.split('=')[0].trim()
        // Only check Supabase auth cookies
        if (name.startsWith('sb-') && name.includes('-auth-token')) {
            if (!isOurs(name) && (isLegacyCustomFamily(name) || !name.startsWith(expectedPrefix))) {
                foreignCookies.push(name)
            }
        }
    }

    if (foreignCookies.length > 0) {
        logger.warn('Supabase', `Cleaning up ${foreignCookies.length} cookies from OTHER Supabase projects. Current: ${currentProjectId}`, {
            cookies: foreignCookies.slice(0, 10).join(', ') + (foreignCookies.length > 10 ? '...' : '')
        })

        const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
        const domains = getAllCookieDomains()
        for (const name of foreignCookies) {
            // Clear on ALL possible domains to ensure removal (from centralized config)
            document.cookie = `${name}=; path=/; ${expiry}`
            for (const domain of domains) {
                document.cookie = `${name}=; path=/; domain=${domain}; ${expiry}`
            }
        }
    }

    // A former config may have written this environment's namespaced session
    // cookie on a broader parent Domain. Once the canonical env cookie is also
    // present, auth-js keeps reading the stale duplicate after every refresh and
    // loops /token + /user. The key is environment-unique, so clear only exact
    // duplicated own-session names from obsolete scopes.
    const legacyScopedOwnCookies = ownAuthCookieNamesForLegacyScopeCleanup(
        parsedCookies,
        ownStorageKey,
    )
    legacyScopedOwnCookies.forEach(clearLegacyOwnAuthCookieScopes)
}

function clearLegacyOwnAuthCookieScopes(name: string): void {
    if (typeof document === 'undefined') return
    const canonicalDomain = getCookieDomain()
    // On localhost/IP hosts the host-only cookie IS canonical. Clearing it
    // after every save immediately erases a successful login's session.
    // Proved through real login + SSR profile in offline-profile.spec.ts.
    if (!canonicalDomain) return
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
    // Clear a host-only legacy copy. The canonical Domain cookie is unaffected.
    document.cookie = `${name}=; path=/; ${expiry}`
    for (const legacyDomain of broaderCookieDomains(canonicalDomain)) {
        document.cookie = `${name}=; path=/; domain=${legacyDomain}; ${expiry}`
    }
}

/**
 * Run cookie cleanup on demand (e.g., on app load or after auth errors)
 * Can be called from anywhere to ensure foreign cookies are cleaned up
 * This helps prevent HTTP 431 "Request Header Fields Too Large" errors
 */
export function runCookieCleanup(): void {
    if (typeof window === 'undefined') return

    const supabaseUrl = deriveSupabaseUrlFromHostname() || process.env.NEXT_PUBLIC_SUPABASE_URL
    // Use 'neutralaiz' as fallback because that's our custom storage key prefix
    const currentProjectId = extractSupabaseProjectId(supabaseUrl) || 'neutralaiz'
    cleanupForeignProjectCookies(currentProjectId)
}

/**
 * Clear all Supabase auth state when stuck in error loop
 * This breaks the infinite 429 retry cycle
 */
function clearAuthState() {
    if (typeof document === 'undefined') return

    // Clear all Supabase-related cookies on ALL possible domains (from centralized config).
    // EXCEPT an in-flight login's PKCE `<storageKey>-code-verifier`: it is written
    // the instant the user clicks SSO and read by /auth/callback a few seconds later;
    // wiping it here (a dead-session cleanup racing that round-trip) makes the
    // exchange fail with "code verifier should be non-empty" (staging 2026-08-19).
    const cookies = document.cookie.split(';')
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
    const domains = getAllCookieDomains()
    for (const cookie of cookies) {
        const name = cookie.split('=')[0].trim()
        if (isClearableAuthStorageKey(name)) {
            document.cookie = `${name}=; path=/; ${expiry}`
            for (const domain of domains) {
                document.cookie = `${name}=; path=/; domain=${domain}; ${expiry}`
            }
        }
    }

    // Clear localStorage auth items
    if (typeof localStorage !== 'undefined') {
        const keysToRemove: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && isClearableAuthStorageKey(key)) {
                keysToRemove.push(key)
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
    }

    logger.warn('Supabase', 'Cleared corrupted auth state due to repeated errors')
}

/**
 * Track auth errors and trigger circuit breaker if threshold exceeded
 */
async function trackAuthError(response?: Response) {
    const now = Date.now()

    // Try to get error message from response
    let errorMessage = ''
    let errorCode = ''
    if (response) {
        try {
            const clone = response.clone()
            const data = await clone.json()
            const summary = summarizeAuthErrorBody(data)
            errorMessage = summary.message
            errorCode = summary.code
        } catch (e) {
            // Ignore parse errors
        }
    }

    // A70: GoTrue ended the session for INACTIVITY. Terminal on the first refusal (no retry can revive it, and
    // the auth client stops refreshing after it), so it never reaches the repeat threshold below: hand it to
    // GlobalReauthHandler at once, with the error attached so it lands on /login?reason=idle.
    if (classifyServerEndedSession({ code: errorCode, message: errorMessage }) === 'idle') {
        authErrorCount = 0
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            logger.warn('Supabase', 'Server ended the session for inactivity')
            window.dispatchEvent(new CustomEvent('auth:circuit-breaker', {
                detail: circuitBreakerDetail('session_idle', { code: errorCode, message: errorMessage }),
            }))
        }
        return
    }

    // "Refresh token already used" is benign when it is a RACE between parallel
    // refreshes (the other one won). It is NOT benign when it repeats: the stored
    // token is dead and every attempt will 400 forever — the old unconditional
    // return here produced ~190 identical 400s in ~35 s with no reauth surface
    // (extreme module 08 defect 14, 2026-08-17). After a few in a short window
    // it falls through as the terminal error it is (see refreshFailureClassifier).
    if (isAlreadyUsedMessage(errorMessage)) {
        const kind = classifyAlreadyUsed(alreadyUsedTracker, now)
        if (kind === 'benign_race') {
            logger.debug('Supabase', 'Benign auth race condition detected (refresh token already used). Ignoring.', {
                occurrence: alreadyUsedTracker.count,
            })
            return
        }
        logger.warn('Supabase', 'Refresh token "already used" is repeating — treating as a dead session', {
            occurrence: alreadyUsedTracker.count,
        })
    }

    const status = response?.status
    const normalizedMessage = errorMessage.toLowerCase()
    const isTerminalAuthError =
        normalizedMessage.includes('invalid refresh token') ||
        normalizedMessage.includes('refresh_token_not_found') ||
        normalizedMessage.includes('invalid_grant') ||
        normalizedMessage.includes('token has expired') ||
        normalizedMessage.includes('jwt malformed')

    // 5xx errors (502, 503, 504) are server-side Supabase issues, NOT client auth problems.
    // Never count these toward the circuit breaker - a brief Supabase outage shouldn't force-logout users.
    if (status && status >= 500) {
        logger.warn('Supabase', 'Supabase server error (not counting toward circuit breaker)', {
            status,
            message: errorMessage,
            url: response?.url,
        })
        return
    }

    // 429 can be transient under load; do not clear auth state or force logout for rate limiting.
    if (status === 429) {
        logger.warn('Supabase', 'Auth endpoint rate limited; suppressing forced logout', {
            status,
            message: errorMessage,
            url: response?.url,
        })
        return
    }

    // Many 400s from auth endpoints are recoverable/transient. Only treat known terminal
    // token failures as circuit-breaker candidates.
    if (status === 400 && !isTerminalAuthError) {
        logger.debug('Supabase', 'Ignoring non-terminal auth 400 response', {
            status,
            message: errorMessage,
            url: response?.url,
        })
        return
    }

    // Reset counter if outside window
    if (now - lastAuthErrorTime > AUTH_ERROR_WINDOW_MS) {
        authErrorCount = 0
    }

    authErrorCount++
    lastAuthErrorTime = now

    logger.warn('Supabase', 'Auth error tracked', {
        count: `${authErrorCount}/${AUTH_ERROR_THRESHOLD}`,
        status: response?.status,
        message: errorMessage,
        url: response?.url
    })

    // Circuit breaker: clear auth state if too many errors
    if (authErrorCount >= AUTH_ERROR_THRESHOLD) {
        logger.error('Supabase', 'Circuit breaker triggered: auth errors in ms. Last error', { authErrorCount: authErrorCount, AUTH_ERROR_WINDOW_MS: AUTH_ERROR_WINDOW_MS, errorMessage: errorMessage })

        // Activate logger suppression to prevent render cascade
        enterAuthErrorSuppression()

        // Safety: if we don't have a terminal token failure, do not clear session.
        if (!isTerminalAuthError) {
            logger.warn('Supabase', 'Circuit breaker reset without clearing auth (non-terminal errors)', {
                status,
                message: errorMessage,
            })
            authErrorCount = 0
            return
        }

        // Check if user WAS authenticated before clearing state
        const hadAuthSessionInLocalStorage = typeof localStorage !== 'undefined' &&
            Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
                .some(key => key && (key.includes('supabase') || key.includes('sb-')))

        const hadAuthSessionInCookies = typeof document !== 'undefined' &&
            document.cookie.split(';').some(c => c.trim().startsWith('sb-') || c.includes('supabase'))

        const hadAuthSession = hadAuthSessionInLocalStorage || hadAuthSessionInCookies

        if (hadAuthSession) {
            logger.warn('Supabase', 'Clearing potentially corrupted auth state')
            clearAuthState()
            authErrorCount = 0

            if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
                // NEVER redirect on circuit breaker — instead always dispatch
                // the `auth:circuit-breaker` event for the GlobalReauthHandler
                // (mounted in app/template.tsx) to show the in-place ReauthModal.
                // Force-redirecting from a long-running task page (deep-analysis
                // generation, report editing, admin work, etc.) loses all
                // in-flight work, which is unacceptable UX. The ReauthModal
                // lets the user re-authenticate without leaving the page.
                //
                // Previously this only fired the event on /calls/ — every other
                // page got force-redirected. The event is now app-wide.
                logger.warn('Supabase', 'Circuit breaker fired - NOT redirecting (GlobalReauthHandler will show ReauthModal)')
                window.dispatchEvent(new CustomEvent('auth:circuit-breaker', {
                    detail: circuitBreakerDetail('auth_error_loop', { code: errorCode, message: errorMessage }),
                }))
            }
        } else {
            // If they weren't logged in anyway, just reset the counter
            authErrorCount = 0
        }
    }
}

/**
 * Get the cookie domain for cross-subdomain sharing.
 * Host-aware for ANY apex (demo.engageiq.ca, engageiq.neutralaiz.com,
 * portal.neutralaiz.com, …) — getEnvCookieDomain() reads the host-aware
 * RuntimeConfig, so the BROWSER cookie domain matches the server/middleware
 * value for the SAME request (no client/server split-brain). No cross-subdomain
 * cookie for localhost / IP literals.
 *
 * Uses centralized config from @/lib/config/environment
 */
function getCookieDomain(): string | undefined {
    if (typeof window === 'undefined') return undefined

    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        return undefined
    }
    const domain = getEnvCookieDomain()
    // Guard against an unset base domain yielding a bare "." (invalid).
    return domain && domain !== '.' ? domain : undefined
}

/**
 * Check if the current context requires secure cookies
 */
function isSecureContext(): boolean {
    if (typeof window === 'undefined') return false
    return window.location.protocol === 'https:'
}

/**
 * Create a Supabase browser client with cross-subdomain cookie support
 *
 * This enables authentication to work when the app is embedded in an iframe
 * by a parent site on the same parent domain (*.neutralaiz.com)
 *
 * NOTE: Uses singleton pattern to prevent multiple clients from causing
 * refresh token race conditions ("Invalid Refresh Token: Already Used")
 */
export const createClient = (): SupabaseClient => {
    // Return existing instance if available (singleton)
    if (supabaseClientInstance) {
        return supabaseClientInstance
    }

    // URL RESOLUTION PRIORITY:
    // CLIENT-SIDE (browser):
    //   1. deriveSupabaseUrlFromHostname() - For self-hosted: derive from current hostname (MOST RELIABLE)
    //   2. window.__RUNTIME_CONFIG__ (injected at runtime by server)
    //   3. process.env.NEXT_PUBLIC_SUPABASE_URL (build-time fallback - LEAST RELIABLE for multi-env)
    //
    // SERVER-SIDE (SSR):
    //   1. process.env.SUPABASE_PUBLIC_URL (external URL for auth flows)
    //   2. process.env.SUPABASE_URL (internal URL for API calls)
    //   3. process.env.NEXT_PUBLIC_SUPABASE_URL (build-time fallback)
    //
    // NOTE: deriveSupabaseUrlFromHostname() generates URLs like https://api-supabase.{env}.neutralaiz.com
    // This is the MOST RELIABLE method for self-hosted multi-environment setups because it uses the
    // current page hostname to determine the correct Supabase URL, avoiding build-time baking issues.
    const isServer = typeof window === 'undefined';

    let supabaseUrl: string | undefined;
    let supabaseAnonKey: string | undefined;
    let urlSource: string = 'unknown';

    if (isServer) {
        // Server-side: use runtime env vars (available in Node.js)
        // Prefer SUPABASE_PUBLIC_URL (external) over SUPABASE_URL (internal Docker)
        supabaseUrl = process.env.SUPABASE_PUBLIC_URL || process.env.SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
        supabaseAnonKey = process.env.SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
        urlSource = process.env.SUPABASE_PUBLIC_URL ? 'SUPABASE_PUBLIC_URL' :
                    process.env.SUPABASE_URL ? 'SUPABASE_URL' : 'NEXT_PUBLIC_SUPABASE_URL';
    } else {
        // Client-side: derive from hostname, RuntimeConfig, or build-time env var
        // Priority: deriveSupabaseUrlFromHostname > RuntimeConfig > build-time env
        // RuntimeConfig is needed because the Docker image may be built with env vars
        // for a different environment (e.g. staging image reused for demo)
        const getClientConfig = () => {
            const runtimeKey = window.__RUNTIME_CONFIG__?.NEXT_PUBLIC_SUPABASE_ANON_KEY;
            const envKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

            // Prioritize derived URL for self-hosted (neutralaiz.com domains)
            // Then RuntimeConfig (server-injected, matches current environment)
            // Then build-time NEXT_PUBLIC_SUPABASE_URL as last resort
            const { url, source } = resolveBrowserSupabaseUrl();
            const key = runtimeKey || envKey;

            return { url, source, key };
        };

        const clientConfig = getClientConfig();
        supabaseUrl = clientConfig.url;
        urlSource = clientConfig.source;
        supabaseAnonKey = clientConfig.key;
    }

    if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error(
            '[Supabase] CRITICAL: Missing required environment variables. ' +
            `Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY. ` +
            `isServer=${isServer}, urlSource=${urlSource}`
        )
    }

    // Log the URL source for debugging
    logger.debug('Supabase', `Using URL from ${urlSource}: ${supabaseUrl.substring(0, 50)}...`);

    // CRITICAL: Clean up cookies from OTHER Supabase projects before creating client
    // This prevents cookie bloat when multiple services use different Supabase projects
    const currentProjectId = extractSupabaseProjectId(supabaseUrl)
    logger.debug('Supabase', `Creating client for project ${currentProjectId}`)
    cleanupForeignProjectCookies(currentProjectId)

    const domain = getCookieDomain()
    const isSecure = isSecureContext()

    // CRITICAL: Use a consistent storageKey across client and server (client uses
    // the external Supabase URL, server the internal one — so it can't be derived
    // from the URL). It must ALSO be namespaced per environment, or a prod cookie
    // on the parent apex leaks into the staging subdomain and collides — see
    // getAuthStorageKey(). Client + server both key it off BASE_DOMAIN, which they
    // derive identically. (2026-08-03)
    const storageKey = getAuthStorageKey()

    supabaseClientInstance = createBrowserClient(supabaseUrl, supabaseAnonKey, {
        auth: {
            storageKey,
            userStorage: getAuthUserStorage(),
        },
        global: {
            // Intercept fetch to prevent refresh token race conditions, and give every request a deadline
            // (CAUSE-B F1): a request on a dead socket must fail, so supabase-js releases its auth lock and the
            // next attempt opens a fresh connection.
            fetch: async (url, options) => {
                const urlString = getFetchInputUrl(url)
                const deadlineMs = supabaseRequestDeadlineMs(urlString)
                const send = () => deadlineMs === null
                    ? fetch(url, options)
                    : fetchWithDeadline(url, options, deadlineMs, 'Supabase request')

                // Check if this is a token refresh request
                const isRefreshRequest = urlString.includes('/auth/v1/token') &&
                    options?.body?.toString().includes('refresh_token')

                if (isRefreshRequest) {
                    // If another refresh is in progress, wait for it instead of making a duplicate request
                    if (refreshPromise && refreshPromiseUrl === urlString) {
                        logger.debug('Supabase', 'Waiting for existing refresh request to complete')
                        try {
                            const shared = refreshPromise
                            // A waiter owns its cancellation, not the leader's network request.
                            const existingResponse = await withDeadline(
                                () => shared,
                                deadlineMs!,
                                'Supabase refresh wait',
                                getFetchRequestSignal(url, options),
                            )
                            // Clone the response since it may have been consumed
                            return existingResponse.clone()
                        } catch {
                            // Cancellation must not fall through to a replacement refresh.
                            getFetchRequestSignal(url, options)?.throwIfAborted()
                            // If the existing promise failed (or hit its deadline), we'll try our own request
                        }
                    }

                    // This is the first refresh request - create the promise and let others wait
                    const sharedRefresh = send()
                    refreshPromiseUrl = urlString
                    refreshPromise = sharedRefresh
                    // Suppress unhandled rejection warning when stored promise rejects
                    // before a concurrent caller can await it. The actual error is still
                    // propagated via the await below.
                    sharedRefresh.catch(() => {})

                    try {
                        const response = await sharedRefresh

                        // Track 400 errors on refresh (but not "Already Used" which is benign)
                        if (response.status === 400 || response.status === 429) {
                            await trackAuthError(response.clone())
                        }

                        return response
                    } finally {
                        // Clear the lock after a short delay to prevent immediate re-races; only if it is still
                        // OUR refresh (a waiter may already have started a newer one).
                        setTimeout(() => {
                            if (refreshPromise === sharedRefresh) {
                                refreshPromise = null
                                refreshPromiseUrl = null
                            }
                        }, 100)
                    }
                }

                // Non-refresh requests: proceed normally
                const response = await send()

                // Track 429 (rate limit) and 400 (invalid token) on auth endpoints
                if (urlString.includes('/auth/')) {
                    if (response.status === 429 || response.status === 400) {
                        await trackAuthError(response.clone())
                    }
                }

                return response
            },
        },
        cookies: {
            encode: 'tokens-only' as const,
            /**
             * Modern getAll API - returns all cookies as name/value pairs
             * The @supabase/ssr library handles all encoding/decoding internally
             * DO NOT decode values here - the library expects raw cookie values
             * @see https://github.com/supabase/ssr/blob/main/src/createBrowserClient.ts
             */
            getAll() {
                if (typeof document === 'undefined') return []

                const rawCookies = document.cookie
                const cookieList = rawCookies.split('; ').filter(c => c.trim())

                // Debug logging removed - getAll() is called very frequently by Supabase SSR
                // and logging every call floods the console

                return cookieList.map(c => {
                        const [name, ...valueParts] = c.split('=')
                        // Join with '=' in case value contains '=' characters
                        let value = valueParts.join('=')

                        // DEFENSIVE FIX: Detect and repair corrupted session cookies
                        // This can happen when session data is incorrectly formatted
                        // Symptoms: "Cannot create property 'user' on string" error
                        if (name.includes('auth-token') && value) {
                            // Check if value looks like a JSON string that contains another JSON string
                            // e.g., '"{\"access_token\":...}"' (escaped JSON inside a string)
                            if (value.startsWith('"') && value.endsWith('"')) {
                                try {
                                    // Try to parse - if result is a string, it was double-encoded
                                    const parsed = JSON.parse(value)
                                    if (typeof parsed === 'string') {
                                        logger.warn('Supabase', 'Detected double-encoded auth cookie, repairing', {
                                            name,
                                            originalLength: value.length,
                                            parsedLength: parsed.length,
                                        })
                                        // Return the inner value (still encoded once for the library to handle)
                                        value = parsed
                                    }
                                } catch {
                                    // Not valid JSON, leave as-is
                                }
                            }

                            // Check for raw JSON in cookie (should be base64-encoded chunks)
                            // Only check for SPECIFIC known raw JSON session patterns, not generic JSON
                            // Base64 cookies would never start with {"
                            if (value.startsWith('{"access_token"') || value.startsWith('{"token_type"')) {
                                logger.error('Supabase', 'CRITICAL: Auth cookie contains raw JSON session instead of base64 chunks', {
                                    name,
                                    valuePrefix: value.substring(0, 50),
                                    valueLength: value.length,
                                })
                                // Clear this corrupted cookie on ALL possible domains (from centralized config)
                                const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
                                const domains = getAllCookieDomains()
                                document.cookie = `${name}=; path=/; ${expiry}`
                                for (const domain of domains) {
                                    document.cookie = `${name}=; path=/; domain=${domain}; ${expiry}`
                                }
                                return { name: name.trim(), value: '' }
                            }

                            // Check for URL-encoded JSON (another corruption pattern)
                            if (value.startsWith('%7B%22') || value.startsWith('%7b%22')) {
                                logger.error('Supabase', 'CRITICAL: Auth cookie is URL-encoded JSON', {
                                    name,
                                    valuePrefix: value.substring(0, 50),
                                })
                                // Try to decode and check
                                try {
                                    const decoded = decodeURIComponent(value)
                                    if (decoded.startsWith('{"')) {
                                        // It's URL-encoded raw JSON - clear it on ALL domains (from centralized config)
                                        const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
                                        const domains = getAllCookieDomains()
                                        document.cookie = `${name}=; path=/; ${expiry}`
                                        for (const domain of domains) {
                                            document.cookie = `${name}=; path=/; domain=${domain}; ${expiry}`
                                        }
                                        return { name: name.trim(), value: '' }
                                    }
                                } catch {
                                    // Decoding failed, leave as-is
                                }
                            }
                        }

                        return { name: name.trim(), value }
                    })
            },
            /**
             * Modern setAll API - sets multiple cookies at once
             * The @supabase/ssr library handles all encoding/decoding internally
             * Values are already properly encoded by the library
             * @see https://github.com/supabase/ssr/blob/main/src/createBrowserClient.ts
             */
            setAll(cookiesToSet: Array<{
                name: string;
                value: string;
                options?: {
                    maxAge?: number;
                    expires?: string | number | Date;
                    domain?: string;
                    path?: string;
                    sameSite?: boolean | 'none' | 'strict' | 'lax';
                    secure?: boolean;
                };
            }>) {
                if (typeof document === 'undefined') return

                // Never delete an in-flight login's PKCE code-verifier from the
                // browser either: gotrue-js removes it on every _saveSession
                // (successful refresh) and _removeSession, so a second tab
                // refreshing during SSO wiped the verifier the login tab had
                // just written (Sol review 2026-08-19). The server callback
                // explicitly expires the verifier after consuming it.
                withoutCodeVerifierDeletions(cookiesToSet).forEach(({ name, value, options }) => {
                    // Build cookie string with cross-subdomain support
                    // DO NOT encode value - the library has already done this
                    const cookiePath = options?.path || '/'
                    let cookie = `${name}=${value}; path=${cookiePath}`

                    // Keep domain stable with server-side cookie writes
                    const cookieDomain = options?.domain || domain
                    if (cookieDomain) {
                        cookie += `; domain=${cookieDomain}`
                    }

                    // Add SameSite and Secure attributes
                    // Default to Lax (not None) - Lax allows same-site subdomain sharing
                    // which is what we need. Only use None for explicit iframe contexts.
                    const secureCookie = typeof options?.secure === 'boolean' ? options.secure : isSecure
                    const rawSameSite = options?.sameSite
                    const requestedSameSite = typeof rawSameSite === 'string' ? rawSameSite.toLowerCase() : undefined

                    // Detect iframe context: only use SameSite=None when embedded in an iframe
                    const isInIframe = typeof window !== 'undefined' && window.self !== window.top
                    const defaultSameSite = isInIframe ? 'none' : 'lax'

                    const normalizedSameSite = requestedSameSite === 'none' || requestedSameSite === 'strict' || requestedSameSite === 'lax'
                        ? requestedSameSite
                        : defaultSameSite
                    let sameSite = normalizedSameSite === 'none' && !secureCookie
                        ? 'lax'
                        : normalizedSameSite
                    let effectiveSecure = secureCookie

                    // PKCE FIX (2026-07-31): the OAuth code-verifier cookie MUST survive the
                    // CROSS-SITE redirect chain app -> GoTrue -> Google -> GoTrue -> /auth/callback.
                    // SameSite=Lax is stripped across that chain on nested-subdomain layouts
                    // (e.g. staging.engageiq.neutralaiz.com), so the server callback sees no
                    // verifier -> "both auth code and code verifier should be non-empty" -> the
                    // exchange fails -> clear-cookies -> login loop. Force None+Secure for the
                    // verifier ONLY (single-use, bound to the code_challenge -> no CSRF exposure).
                    // Session cookies stay Lax. Only when we can actually set Secure (https).
                    const isPkceVerifier = typeof name === 'string' && name.includes('code-verifier')
                    if (isPkceVerifier && (secureCookie || isSecure)) {
                        sameSite = 'none'
                        effectiveSecure = true
                    }

                    cookie += `; SameSite=${sameSite.charAt(0).toUpperCase()}${sameSite.slice(1)}`
                    if (effectiveSecure) {
                        cookie += '; Secure'
                    }

                    // Add max-age if specified
                    // NOTE: maxAge=0 is valid and must be preserved for deletions.
                    if (typeof options?.maxAge === 'number') {
                        cookie += `; max-age=${options.maxAge}`
                    }

                    // Preserve explicit expires for robust cookie removal paths.
                    if (options?.expires) {
                        const expiresDate = options.expires instanceof Date
                            ? options.expires
                            : new Date(options.expires)
                        if (!Number.isNaN(expiresDate.getTime())) {
                            cookie += `; expires=${expiresDate.toUTCString()}`
                        }
                    }

                    document.cookie = cookie

                    // The first successful canonical save also retires a lone
                    // legacy copy before it can become a same-name refresh loop.
                    if (isOwnAuthCookieName(name, storageKey)) {
                        clearLegacyOwnAuthCookieScopes(name)
                    }
                })
            },
        },
    })

    // The page keeps its own last session in memory (see sessionKeeper); the ended-call gate hands it back if the browser drops the cookie jar.
    supabaseClientInstance.auth.onAuthStateChange((event, session) => observeAuthState(event, session))

    void migrateOversizedSessionCookies(supabaseClientInstance)

    return supabaseClientInstance
}
